<script lang="ts" setup>
import { computed, ref, showErrorMsg, watch } from '#imports';
import { declareProviders, useService } from '@kaokei/use-vue-service';
import BadgeGroup from '~/components/BadgeGroup.vue';
import BadgeWall from '../BadgeWall/BadgeWall.vue';
import CCFLevelTooltip from '../CCFLevelTooltip.vue';
import { UserHoverCardService } from './UserHoverCard.service';

declareProviders([UserHoverCardService]);
const service = useService(UserHoverCardService);

const props = defineProps({
  uid: {
    type: String,
    required: true,
  },
});

const popupVisible = ref(false);

const eventData = computed(() => ({
  others_uid: props.uid,
}));

const HOME_LINK = computed(() => ({
  name: 'oj_home',
  query: {
    id: props.uid,
  },
}));

const showBadgeModal = ref(false);
function handleShowBadgeModal() {
  showBadgeModal.value = true;
  popupVisible.value = false;
}

watch(
  [() => popupVisible.value, () => props.uid],
  ([visible, uid]) => {
    if (visible) {
      if (typeof uid === 'string') {
        service.getMyHomeRecord(uid);
      } else {
        popupVisible.value = false;
      }
    }
  },
  {
    immediate: true,
  },
);
</script>
<template>
  <a-popover
    v-model:open="popupVisible"
    :destroy-tooltip-on-hide="true"
    placement="bottom"
    :overlay-style="{zIndex: 900}"
  >
    <template #content>
      <div
        v-track:newoj_idcard_show="eventData"
        class="min-h-[153px] w-[358px] bg-white px-1 pb-3 pt-2"
      >
        <template v-if="service.data.uid">
          <div class="flex items-center">
            <div>
              <NuxtLink
                v-login
                v-track:newoj_idcard_personalhomepage_click.click="eventData"
                :to="HOME_LINK"
                target="_blank"
                ><a-avatar
                  class="!border-[#ededed]"
                  :size="56"
                  :src="service.data.avatar"
                ></a-avatar>
              </NuxtLink>
            </div>

            <div class="ml-3 overflow-hidden">
              <NuxtLink
                v-login
                v-track:newoj_idcard_personalhomepage_click.click="eventData"
                :to="HOME_LINK"
                target="_blank"
                class="flex items-center"
              >
                <div class="max-w-[243px]">
                  <span
                    class="block truncate text-xl font-bold leading-7 text-colorText"
                  >
                    {{ service.data.userName }}</span
                  >
                </div>
                <CCFLevelTooltip
                  v-if="service.data.ccfLevel"
                  :size="20"
                  :level-text="service.data.ccfLevel"
                  :level-icon="service.data.authenticationIcon"
                ></CCFLevelTooltip>
              </NuxtLink>

              <NuxtLink
                v-if="service.data.description"
                v-login
                v-track:newoj_idcard_personalhomepage_click.click="eventData"
                class="block"
                :to="HOME_LINK"
                target="_blank"
              >
                <p class="max-w-full truncate pt-2 text-colorTextSecondary">
                  {{ service.data.description }}
                </p>
              </NuxtLink>
            </div>
          </div>

          <a-divider class="!my-4"></a-divider>

          <NuxtLink
            v-login
            v-track:newoj_idcard_personalhomepage_click.click="eventData"
            :to="HOME_LINK"
            target="_blank"
            class="flex items-center justify-between text-center"
          >
            <div>
              <div class="text-sm leading-none text-subtitle">通过数</div>
              <div class="mt-2 text-xl font-bold leading-none text-title">
                {{ service.data.acCount }}
              </div>
            </div>
            <div>
              <div class="text-sm leading-none text-subtitle">比赛数</div>
              <div class="mt-2 text-xl font-bold leading-none text-title">
                {{ service.data.contestCount }}
              </div>
            </div>
            <div>
              <div class="text-sm leading-none text-subtitle">发帖数</div>
              <div class="mt-2 text-xl font-bold leading-none text-title">
                {{ service.data.posterCount }}
              </div>
            </div>
            <div>
              <div class="text-sm leading-none text-subtitle">题解数</div>
              <div class="mt-2 text-xl font-bold leading-none text-title">
                {{ service.data.problemSolutionCount }}
              </div>
            </div>
          </NuxtLink>

          <div
            v-if="service.data.badgeVoList?.length"
            v-login
            v-track:newoj_idcard_goods_click.click="eventData"
            class="mt-5 flex h-10 cursor-pointer items-center justify-between"
            @click="handleShowBadgeModal"
          >
            <BadgeGroup :list="service.data.badgeVoList" />
            <div class="flex items-center justify-start">
              <span class="text-colorTextSecondary"
                >{{ service.data.badgeCount }}枚谷子</span
              >
              <img
                class="h-2 w-2"
                src="~/assets/image/icon/icon-arrow-right-small.svg"
                alt="arrow"
              />
            </div>
          </div>
        </template>
        <template v-else>
          <a-spin>
            <div class="min-h-[153px]"></div>
          </a-spin>
        </template>
      </div>
    </template>

    <slot></slot>

    <BadgeWall
      v-model:open="showBadgeModal"
      :uid="uid"
      :user-name="service.data.userName"
    />
  </a-popover>
</template>
<style scoped></style>
